/**
 * Pitch deck, ranking, legality and visibility — the small pieces the module
 * is assembled from.
 */
import { SUITS, cardId, rankOf, suitOf } from '../../core/cards.js';
import { anyOf } from '../../core/legality.js';
import { emptyTrick } from '../../core/trick.js';
import { PITCH_CARDS_EACH } from './config.js';
/** Canonical order: suits in core SUITS order, ranks ascending. The fairness
 *  commitment hashes this, so it must never change. */
export function pitchDeck(_config) {
    const cards = [];
    for (const suit of SUITS)
        for (let r = 2; r <= 14; r++)
            cards.push(cardId(r, suit));
    return Object.freeze(cards);
}
/** Three and three from the dealer's left, no widow (rules §2.1). */
export function pitchDealPlan(_config) {
    const half = PITCH_CARDS_EACH / 2;
    return { steps: [{ each: half }, { each: half }], firstSeatOffset: 1 };
}
/** Plain rank order everywhere; the only context is which suit is trump, and
 *  before the first lead there is none. */
export function pitchRanking(extra) {
    const trump = extra?.trump ?? null;
    return {
        trumpSuit: trump,
        effectiveSuit: (card) => suitOf(card),
        isTrump: (card) => trump !== null && suitOf(card) === trump,
        strength: (card) => rankOf(card) ?? 0,
    };
}
export function pitchCurrentTrick(hand) {
    return hand.tricks[hand.tricks.length - 1] ?? emptyTrick(0, hand.dealer);
}
/**
 * Rules §2.3. The leader may lead anything — the very first lead of the hand
 * is what makes a suit trump. After that:
 *  - trump led: follow trump if you can, else anything;
 *  - plain suit led: follow it OR trump, even while holding the suit led;
 *    only when you have neither may you throw off.
 * `trumpIn: 'ifVoid'` is strict follow-suit (Smear).
 */
export function pitchLegalPlays(holding, trick, ctx, config) {
    if (trick.plays.length === 0 || trick.ledSuit === null)
        return anyOf(holding);
    const led = trick.ledSuit;
    const follow = holding.filter((c) => ctx.effectiveSuit(c) === led);
    if (config.trumpIn === 'ifVoid' || led === ctx.trumpSuit) {
        return anyOf(follow.length > 0 ? follow : holding);
    }
    if (follow.length === 0)
        return anyOf(holding);
    return anyOf(holding.filter((c) => ctx.effectiveSuit(c) === led || ctx.isTrump(c)));
}
/** Only a card that is neither the suit led nor trump proves a void, unless
 *  the table plays strict follow-suit. */
export function pitchProvesVoid(ctx, _led, card, config) {
    return config.trumpIn === 'ifVoid' || !ctx.isTrump(card);
}
function handOver(hand, matchFinished) {
    return matchFinished || hand.phase === 'handComplete' || hand.phase === 'abandoned';
}
/** Your own hand, and everyone's once the hand is over. Nothing else: there is
 *  no widow, and the 28 undealt cards stay in the stock unseen. */
export const pitchVisibility = {
    holdings(hand, viewerSeat, matchFinished) {
        const over = handOver(hand, matchFinished);
        return hand.secret.holdings.map((_, seat) => (over || seat === viewerSeat ? 'cards' : 'count'));
    },
    kitty: () => 'hidden',
    discards: () => 'hidden',
};
//# sourceMappingURL=rules.js.map